import React, { useMemo, type ReactNode } from 'react';
import Button from '@atlaskit/button';
import FileIcon from '@atlaskit/icon/glyph/file';
import { Inline } from '@atlaskit/primitives';
import type { ProjectType } from '@atlassian/jira-common-constants';
import { useIntl } from '@atlassian/jira-intl';
import {
	MAPPING_GUIDE_BUSINESS,
	MAPPING_GUIDE_SOFTWARE,
	STRUCTURE_CSV_LINK,
} from '../../constants';
import { CSV, DEFAULT, type ImportType } from '../../constants/import-type';
import { ProjectTypes } from '../../constants/project';
import { getSourceItemMap } from '../../constants/source-items';
import { renderIcon } from '../../ui/icon';
import messages from './messages';

export const useContentForCSV = (importType?: ImportType, projectType?: ProjectType | null) => {
	const { formatMessage } = useIntl();

	return useMemo(() => {
		const mappingLink =
			projectType === ProjectTypes.SOFTWARE ? MAPPING_GUIDE_SOFTWARE : MAPPING_GUIDE_BUSINESS;

		if (!importType || importType === CSV || importType === DEFAULT) {
			return {
				uploadScreenDescription: formatMessage(messages.uploadScreenDescription, {
					link: (content: ReactNode) => (
						<Button href={STRUCTURE_CSV_LINK} target="_blank" appearance="link" spacing="none">
							{content}
						</Button>
					),
					b: (content: ReactNode) => <b>{content}</b>,
				}),
				uploadScreenDescriptionNew: formatMessage(messages.uploadScreenDescriptionNew, {
					link: (content: ReactNode) => (
						<Button href={STRUCTURE_CSV_LINK} target="_blank" appearance="link" spacing="none">
							{content}
						</Button>
					),
					b: (content: ReactNode) => <b>{content}</b>,
				}),
				setupProjectScreenDescription: formatMessage(messages.setupProjectScreenDescription),
				usersMappingScreenColumnHeader: (numberOfUsers: number) => (
					<Inline space="space.050" alignBlock="center">
						<FileIcon label="" size="medium" />
						{`${formatMessage(messages.usersMappingScreenColumnHeader)} (${numberOfUsers})`}
					</Inline>
				),
				fieldMappingDescription: formatMessage(messages.fieldMappingDescription, {
					link: (content: ReactNode) => (
						<Button href={mappingLink} target="_blank" appearance="link" spacing="none">
							{content}
						</Button>
					),
				}),
				fieldMappingDescriptionWithConfig: formatMessage(
					messages.fieldMappingDescriptionWithConfig,
					{
						link: (content: ReactNode) => (
							<Button href={mappingLink} target="_blank" appearance="link" spacing="none">
								{content}
							</Button>
						),
					},
				),
				fieldMappingTableHeading: (
					<Inline space="space.050" alignBlock="center">
						<FileIcon label="" size="medium" />
						{formatMessage(messages.fieldMappingTableHeading)}
					</Inline>
				),
			};
		}
		const sources = getSourceItemMap(formatMessage);
		const sourceName = sources[importType]?.name || importType;
		const sourceIcon = sources[importType]?.icon || sources[CSV]?.icon;
		return {
			uploadScreenDescription: formatMessage(messages.uploadScreenCustomisedDescription, {
				importType: sourceName,
			}),
			uploadScreenDescriptionNew: formatMessage(messages.uploadScreenCustomisedDescriptionNew, {
				importType: sourceName,
			}),
			setupProjectScreenDescription: formatMessage(
				messages.setupProjectScreenCustomisedDescription,
				{
					importType: sourceName,
				},
			),
			usersMappingScreenColumnHeader: (numberOfUsers: number) => (
				<Inline space="space.050" alignBlock="center">
					{renderIcon(sourceIcon, true)}
					{`${formatMessage(messages.usersMappingScreenCustomisedColumnHeader, {
						importType: sourceName,
					})} (${numberOfUsers})`}
				</Inline>
			),
			fieldMappingDescription: formatMessage(messages.fieldMappingCustomisedDescription, {
				link: (content: ReactNode) => (
					<Button href={mappingLink} target="_blank" appearance="link" spacing="none">
						{content}
					</Button>
				),
				importType: sourceName,
			}),
			fieldMappingDescriptionWithConfig: formatMessage(
				messages.fieldMappingCustomisedDescriptionWithConfig,
				{
					link: (content: ReactNode) => (
						<Button href={mappingLink} target="_blank" appearance="link" spacing="none">
							{content}
						</Button>
					),
					importType: sourceName,
				},
			),
			fieldMappingTableHeading: (
				<Inline space="space.050" alignBlock="center">
					{renderIcon(sourceIcon, true)}
					{formatMessage(messages.fieldMappingTableCustomisedHeading, {
						importType: sourceName,
					})}
				</Inline>
			),
		};
	}, [formatMessage, importType, projectType]);
};
